// リレー転送 HTTP クライアント（M4）。
// 暗号化はここでは行わない（crypto.ts と組み合わせて使う）。
// 進捗コールバック・キャンセル（AbortSignal）に対応する。
//
// エンドポイント仕様は relay/README.md を参照。

/**
 * リレーの既定 URL。
 * 本番 Worker (facemaker-relay) は未デプロイのため、既定は空文字列＝「未設定」。
 * 設定画面（localStorage）で URL を入れるまでリレー機能はエラーを返す。
 * デプロイ後はここに `https://facemaker-relay.<subdomain>.workers.dev` を書く。
 */
export const DEFAULT_RELAY_BASE = "https://facemaker-relay.ts0604626.workers.dev";

const LS_KEY = "facemaker.relayBaseUrl";

/** 設定済みのリレー URL（末尾スラッシュなし）。未設定なら空文字列。 */
export function getRelayBaseUrl(): string {
  let stored = "";
  try {
    stored = localStorage.getItem(LS_KEY) ?? "";
  } catch {
    /* プライベートブラウジング等では localStorage が使えない */
  }
  return (stored || DEFAULT_RELAY_BASE).trim().replace(/\/+$/, "");
}

/** リレー URL を保存する（空文字列で既定に戻す）。 */
export function setRelayBaseUrl(url: string): void {
  try {
    const v = url.trim().replace(/\/+$/, "");
    if (v) localStorage.setItem(LS_KEY, v);
    else localStorage.removeItem(LS_KEY);
  } catch {
    /* 保存できなくても今回のセッションでは使えないだけ */
  }
}

export class RelayError extends Error {
  readonly status?: number;
  constructor(message: string, status?: number) {
    super(message);
    this.name = "RelayError";
    this.status = status;
  }
}

export interface UploadResult {
  code: string;
  expiresAt: string;
  /** 送信者取り消し用トークン（DELETE /v1/files/{code}）。 */
  deleteToken: string;
}

interface Options {
  baseUrl?: string;
  signal?: AbortSignal;
}

function base(options: Options): string {
  const b = (options.baseUrl ?? getRelayBaseUrl()).replace(/\/+$/, "");
  if (!b) {
    throw new RelayError(
      "リレーの URL が設定されていません。「書き出し」タブの詳細設定でリレー URL を入力してください。",
    );
  }
  return b;
}

function networkError(e: unknown): never {
  if ((e as Error).name === "AbortError") throw e;
  throw new RelayError("リレーに接続できません。オフラインの場合はファイルでの受け渡しを使ってください。");
}

/** 暗号化済みバイト列をアップロードして6桁コードを得る。 */
export async function uploadToRelay(cipherBytes: ArrayBuffer, options: Options = {}): Promise<UploadResult> {
  const url = `${base(options)}/v1/files`;
  let res: Response;
  try {
    res = await fetch(url, {
      method: "PUT",
      body: cipherBytes,
      signal: options.signal,
      headers: { "Content-Type": "application/octet-stream" },
    });
  } catch (e) {
    networkError(e);
  }
  if (res.status === 413) {
    throw new RelayError("ファイルが大きすぎます（100MBまで）。ファイルでの受け渡しを使ってください。", 413);
  }
  if (res.status === 429) throw new RelayError(await rateLimitMessage(res), 429);
  if (!res.ok) throw new RelayError(`アップロードに失敗しました（HTTP ${res.status}）。`, res.status);
  const body = (await res.json()) as { code?: string; expiresAt?: string; deleteToken?: string };
  if (!body.code) throw new RelayError("リレーの応答が不正です。");
  return { code: body.code, expiresAt: body.expiresAt ?? "", deleteToken: body.deleteToken ?? "" };
}

/** 受信箱（Unity が予約した箱）へ暗号化済みバイト列を投函する。 */
export async function fillMailbox(code: string, cipherBytes: ArrayBuffer, options: Options = {}): Promise<void> {
  const url = `${base(options)}/v1/mailboxes/${encodeURIComponent(code)}`;
  let res: Response;
  try {
    res = await fetch(url, {
      method: "PUT",
      body: cipherBytes,
      signal: options.signal,
      headers: { "Content-Type": "application/octet-stream" },
    });
  } catch (e) {
    networkError(e);
  }
  if (res.status === 404 || res.status === 409) {
    throw new RelayError(
      "受信待ちが見つかりません（期限切れ、既に受信済み、または Unity 側でキャンセルされた可能性があります）。",
      res.status,
    );
  }
  if (res.status === 413) {
    throw new RelayError("ファイルが大きすぎます（100MBまで）。ファイルでの受け渡しを使ってください。", 413);
  }
  if (res.status === 429) throw new RelayError(await rateLimitMessage(res), 429);
  if (!res.ok) throw new RelayError(`送信に失敗しました（HTTP ${res.status}）。`, res.status);
}

/**
 * 送信済みの転送を取り消す。
 * 404（期限切れ・受信済み・取り消し済み）は「既に存在しない」＝目的達成として成功扱い。
 */
export async function revokeUpload(code: string, deleteToken: string, options: Options = {}): Promise<void> {
  const url = `${base(options)}/v1/files/${encodeURIComponent(code)}`;
  let res: Response;
  try {
    res = await fetch(url, {
      method: "DELETE",
      signal: options.signal,
      headers: { "X-Delete-Token": deleteToken },
    });
  } catch (e) {
    networkError(e);
  }
  if (res.ok || res.status === 404) return;
  throw new RelayError(`取り消しに失敗しました（HTTP ${res.status}）。`, res.status);
}

export interface UploadStatus {
  code: string;
  alive: boolean;
  expiresAt?: string;
}

/**
 * 自分がアップロードしたものがまだリレーに残っているかをまとめて確認する（非破壊、最大20件）。
 * 受信済み・取り消し済み・期限切れは alive=false になる。
 */
export async function checkUploadStatus(
  items: Array<{ code: string; deleteToken: string }>,
  options: Options = {},
): Promise<UploadStatus[]> {
  if (items.length === 0) return [];
  let res: Response;
  try {
    res = await fetch(`${base(options)}/v1/files/status`, {
      method: "POST",
      signal: options.signal,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items }),
    });
  } catch (e) {
    networkError(e);
  }
  if (!res.ok) throw new RelayError(`リレーの状態確認に失敗しました（HTTP ${res.status}）。`, res.status);
  const body = (await res.json()) as { items?: UploadStatus[] };
  return body.items ?? [];
}

/** 6桁コードで暗号化済みバイト列をダウンロードする（成功するとサーバー側で削除される）。 */
export async function downloadFromRelay(
  code: string,
  options: Options & { onProgress?: (loaded: number, total: number) => void } = {},
): Promise<ArrayBuffer> {
  const url = `${base(options)}/v1/files/${encodeURIComponent(code)}`;
  let res: Response;
  try {
    res = await fetch(url, { signal: options.signal });
  } catch (e) {
    networkError(e);
  }
  if (res.status === 404) {
    throw new RelayError("コードが見つかりません（期限切れ、または既に受信済みの可能性があります）。", 404);
  }
  if (res.status === 429) throw new RelayError(await rateLimitMessage(res), 429);
  if (!res.ok) throw new RelayError(`ダウンロードに失敗しました（HTTP ${res.status}）。`, res.status);

  const total = Number(res.headers.get("Content-Length") ?? "0");
  if (res.body && options.onProgress) {
    const reader = res.body.getReader();
    const chunks: Uint8Array[] = [];
    let loaded = 0;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      loaded += value.length;
      options.onProgress(loaded, total);
    }
    const out = new Uint8Array(loaded);
    let off = 0;
    for (const c of chunks) {
      out.set(c, off);
      off += c.length;
    }
    return out.buffer;
  }
  return res.arrayBuffer();
}

/** 429 のときはリレーが返す日本語メッセージをそのまま使う（待ち時間の目安が入っている）。 */
async function rateLimitMessage(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string };
    if (body.error) return body.error;
  } catch {
    /* JSON でなければ既定文言 */
  }
  return "リレーが混み合っています。しばらく待ってから再試行してください。";
}
